/**
 * 숨겨진 file input + RDS Button 조합.
 * 파일 업로드 UI를 프로젝트 전반에서 버튼 형태로 통일한다.
 */
import { Button, type ButtonProps } from '@/shared/components/ui/button';
import { Upload } from 'lucide-react';
import React, { useRef } from 'react';

export interface FileUploadButtonProps
  extends Omit<ButtonProps, 'type' | 'onChange' | 'onClick' | 'children'> {
  accept?: string;
  multiple?: boolean;
  inputId?: string;
  /** 외부에서 input을 리셋·트리거할 때 사용 */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** 선택 후 동일 파일 재선택 가능하도록 input value 초기화 */
  resetOnSelect?: boolean;
  showUploadIcon?: boolean;
  children?: React.ReactNode;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFilesSelected?: (files: FileList | null) => void;
}

export const FileUploadButton: React.FC<FileUploadButtonProps> = ({
  accept,
  multiple,
  inputId,
  inputRef: inputRefProp,
  resetOnSelect = true,
  showUploadIcon = true,
  children = '파일 선택',
  onChange,
  onFilesSelected,
  disabled,
  className,
  variant = 'outline',
  size = 'sm',
  ...buttonProps
}) => {
  const fallbackRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? fallbackRef;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    onFilesSelected?.(event.target.files);
    if (resetOnSelect) {
      event.target.value = '';
    }
  };

  return (
    <div className="w-fit">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        disabled={disabled}
        onChange={handleChange}
      />
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        {...buttonProps}
      >
        {showUploadIcon ? <Upload /> : null}
        {children}
      </Button>
    </div>
  );
};
